import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Descriptions, Drawer, Flex, Select, Skeleton, Space, Table, Tag, Typography } from "antd";
import { api, unwrap, type ChannelConnectionDetail } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { useWarehouses } from "@/lib/useWarehouses";
import { useConnect } from "./hooks";
import { PlatformLabel } from "./PlatformBadge";
import { ConnectionStatus } from "./StatusTag";
import { StoreFulfillment } from "./StoreFulfillment";

type Job = ChannelConnectionDetail["syncJobs"][number];
type Event = ChannelConnectionDetail["recentEvents"][number];

/** "Detail toko" (App Flow §5): status, last sync, latest errors, actions. */
export function ConnectionDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t, tDynamic, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const connect = useConnect();
  const tz = me?.workspace?.timezone;
  const manage = can("channels.manage");

  const detail = useQuery({
    queryKey: ["channels", "connection", id],
    queryFn: () => unwrap(api.GET("/v1/channels/connections/{connectionID}", { params: { path: { connectionID: id! } } })),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data?.syncJobs.some((j) => j.status === "running") ? 3000 : false),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["channels"] });
  const warehouses = useWarehouses();

  const setWarehouse = useMutation({
    mutationFn: (warehouseId: string | null) =>
      unwrap(api.PUT("/v1/channels/connections/{connectionID}/warehouse", { params: { path: { connectionID: id! } }, body: { warehouseId } })),
    onSuccess: () => {
      void message.success(t("channels.warehouseSaved"));
      void refresh();
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const rename = useMutation({
    mutationFn: (name: string) => unwrap(api.PUT("/v1/channels/manual/{connectionID}", { params: { path: { connectionID: id! } }, body: { name } })),
    onSuccess: () => void refresh(),
    onError: (err) => void message.error(errorText(err)),
  });

  const sync = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/channels/connections/{connectionID}/sync", { params: { path: { connectionID: id! } } })),
    onSuccess: () => {
      void message.success(t("channels.syncQueued"));
      // The worker picks the job up within seconds; check back shortly.
      window.setTimeout(() => void refresh(), 2500);
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const disconnect = () => {
    const c = detail.data?.connection;
    if (!c) return;
    modal.confirm({
      title: t("channels.disconnect"),
      content: t("channels.disconnectConfirm", { name: c.name }),
      okText: t("channels.disconnect"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        try {
          await unwrap(api.DELETE("/v1/channels/connections/{connectionID}", { params: { path: { connectionID: c.id } } }));
          void message.success(t("channels.disconnected", { name: c.name }));
          onClose();
          await refresh();
        } catch (err) {
          void message.error(errorText(err));
        }
      },
    });
  };

  const d = detail.data;
  const c = d?.connection;
  return (
    <Drawer open={id !== null} onClose={onClose} size="large" title={c?.name ?? t("channels.detail")} destroyOnHidden>
      {!d || !c ? (
        <Skeleton active />
      ) : (
        <Space orientation="vertical" size={24} style={{ width: "100%" }}>
          {c.status === "reauth_required" && (
            <Alert
              type="warning"
              showIcon
              title={t("channels.reauthHelp")}
              action={
                manage && (
                  <Button type="primary" onClick={() => connect.mutate(c.platform)} loading={connect.isPending}>
                    {t("channels.reconnect")}
                  </Button>
                )
              }
            />
          )}
          {c.lastError && c.status !== "reauth_required" && (
            <Alert
              type="error"
              showIcon
              title={t("channels.lastError")}
              description={`${c.lastErrorAt ? formatDateTime(c.lastErrorAt, tz) + " · " : ""}${c.lastError}`}
            />
          )}

          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={t("common.status")}>
              <ConnectionStatus status={c.status} />
            </Descriptions.Item>
            <Descriptions.Item label={t("channels.colMarketplace")}>
              <PlatformLabel platform={c.platform} />
            </Descriptions.Item>
            {c.platform === "manual" ? (
              <Descriptions.Item label={t("common.name")}>
                {manage ? (
                  <Typography.Text editable={{ onChange: (v) => v.trim().length >= 2 && v.trim() !== c.name && rename.mutate(v.trim()) }}>{c.name}</Typography.Text>
                ) : (
                  c.name
                )}
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label={t("channels.shopId")}>
                <span className="z-num">{c.externalShopId}</span>
              </Descriptions.Item>
            )}
            {warehouses.active.length > 0 && (
              <Descriptions.Item label={t("channels.warehouse")}>
                {manage ? (
                  <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                    <Select<string>
                      value={c.warehouseId ?? ""}
                      onChange={(v) => setWarehouse.mutate(v || null)}
                      loading={setWarehouse.isPending}
                      style={{ minWidth: 240 }}
                      options={[
                        { value: "", label: t("channels.warehouseDefault") },
                        ...warehouses.active.map((w) => ({ value: w.id, label: w.isDefault ? `${w.name} ★` : w.name })),
                      ]}
                    />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t("channels.warehouseHelp")}
                    </Typography.Text>
                  </Space>
                ) : (
                  (warehouses.nameOf(c.warehouseId) ?? t("channels.warehouseDefault"))
                )}
              </Descriptions.Item>
            )}
            {c.platform !== "manual" && (
              <Descriptions.Item label={t("channels.lastSync")}>
                {c.lastSyncAt ? formatDateTime(c.lastSyncAt, tz) : t("common.never")}
              </Descriptions.Item>
            )}
            {c.platform !== "manual" && (
              <Descriptions.Item label={t("channels.lastWebhook")}>
                {c.lastWebhookAt ? formatDateTime(c.lastWebhookAt, tz) : t("common.never")}
              </Descriptions.Item>
            )}
            {c.platform !== "manual" && <Descriptions.Item label={t("channels.eventCounts")}>
              <Space wrap>
                {Object.entries(d.eventCounts).map(([kind, n]) => (
                  <Tag key={kind}>
                    {tDynamic(`channels.kind.${kind}`, "channels.kind.other")}: <span className="z-num">{n}</span>
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>}
          </Descriptions>

          {c.platform === "manual" ? (
            <Alert type="info" showIcon title={t("channels.manual.drawerHelp")} />
          ) : (
            <StoreFulfillment connection={c} manage={manage} />
          )}

          {manage && (
            <Flex gap={12}>
              {c.platform !== "manual" && (
                <Button
                  type="primary"
                  onClick={() => sync.mutate()}
                  loading={sync.isPending}
                  disabled={c.status === "reauth_required" || d.syncJobs.some((j) => j.status === "running")}
                >
                  {t("channels.syncNow")}
                </Button>
              )}
              <Button danger onClick={disconnect}>
                {t("channels.disconnect")}
              </Button>
            </Flex>
          )}

          <div>
            <Typography.Title level={3} style={{ fontSize: 16 }}>
              {t("channels.syncJobs")}
            </Typography.Title>
            <Table<Job>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.syncJobs}
              columns={[
                { title: t("logs.time"), dataIndex: "startedAt", render: (v: string) => formatDateTime(v, tz) },
                { title: t("logs.action"), dataIndex: "kind", render: (k: string) => tDynamic(`channels.job.${k}`, "channels.job.manual") },
                {
                  title: t("common.status"),
                  dataIndex: "status",
                  render: (s: Job["status"], j) => (
                    <Space orientation="vertical" size={0}>
                      <Tag color={s === "succeeded" ? "success" : s === "failed" ? "error" : "processing"}>{t(`channels.job.${s}`)}</Tag>
                      {j.error && <Typography.Text type="danger" style={{ fontSize: 12 }}>{j.error}</Typography.Text>}
                    </Space>
                  ),
                },
                {
                  title: t("logs.detail"),
                  render: (_, j) => (
                    <span className="z-num">
                      {t("channels.jobResult", { orders: j.ordersSeen, listings: j.listingsSeen, new: j.newEvents })}
                    </span>
                  ),
                },
              ]}
            />
          </div>

          <div>
            <Typography.Title level={3} style={{ fontSize: 16 }}>
              {t("channels.events")}
            </Typography.Title>
            <Table<Event>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.recentEvents}
              columns={[
                { title: t("logs.time"), dataIndex: "receivedAt", render: (v: string) => formatDateTime(v, tz) },
                { title: t("channels.colKind"), dataIndex: "kind", render: (k: string) => tDynamic(`channels.kind.${k}`, "channels.kind.other") },
                { title: "ID", dataIndex: "externalId", render: (v: string) => <span className="z-num">{v}</span> },
                { title: t("channels.colSource"), dataIndex: "source", render: (v: string) => tDynamic(`channels.source.${v}`, "channels.kind.other") },
              ]}
            />
          </div>
        </Space>
      )}
    </Drawer>
  );
}
